/**
 * usePublicPricing
 *
 * Real prices and trial length for the marketing pages, from GET /api/public/pricing (DC-50).
 * Fetched in an effect — i.e. only AFTER hydration — so the prerendered HTML never contains a
 * number: until the request resolves the pages render a neutral skeleton (BR-107). No price or
 * trial length is hard-coded anywhere in apps/web.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { PublicPricingResponseSchema, type PublicPricingResponse } from '@resida/api-types/billing';

export type PublicPricingState =
  | { status: 'loading' }
  | { status: 'ready'; pricing: PublicPricingResponse }
  | { status: 'error' };

const PRICING_URL = '/api/public/pricing';

const LOADING: PublicPricingState = { status: 'loading' };

// One request and one result shared by every component on the page (home, pricing page, calculator),
// so a retry from any of them updates all of them.
let snapshot: PublicPricingState = LOADING;
let inFlight = false;
const listeners = new Set<() => void>();

function publish(next: PublicPricingState): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Fetch the pricing unless it is already loaded or on its way; a failed earlier attempt is retried. */
function load(): void {
  if (inFlight || snapshot.status === 'ready') return;
  inFlight = true;
  if (snapshot.status === 'error') publish(LOADING);
  fetch(PRICING_URL, { headers: { Accept: 'application/json' } })
    .then((response) => {
      if (!response.ok) throw new Error(`Pricing request failed: ${response.status}`);
      return response.json();
    })
    // An old-server or cached response with a different shape is an error state, not a crash.
    .then((body: unknown) => PublicPricingResponseSchema.parse(body))
    .then(
      (pricing): PublicPricingState => ({ status: 'ready', pricing }),
      (): PublicPricingState => ({ status: 'error' }),
    )
    .then((next) => {
      inFlight = false;
      publish(next);
    });
}

/** Try the pricing request again after an error (shared by every component using the pricing). */
export function retryPublicPricing(): void {
  load();
}

export function usePublicPricing(): PublicPricingState {
  // Fetched in an effect, i.e. only after hydration; the server snapshot is always "loading".
  useEffect(load, []);
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => LOADING,
  );
}

/** 1000 -> "$10", 1050 -> "$10.50", 0 -> "Free". */
export function formatPriceCents(cents: number): string {
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}
